import { Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../../components/ActionButton'
import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { FilterBar, FilterSearch, FilterSelect } from '../../../../components/FilterBar'
import { TableSkeletonRows } from '../../../../components/Loading'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../../components/paginator'
import { TableEmptyRow, TableErrorRow } from '../../../../components/TableEmptyRow'
import { formatDateTime, useHasRole, useAppToast } from '../../../../helpers'
import UrlShortenerService, { IUtm } from '../../../../services/UrlShortener/UrlShortenerService'
import UtmListFormModal from './UtmListFormModal'

interface IUtmListPageProps {
  used_by: 'commercial' | 'marketing'
}

const UTM_LABELS: Record<IUtm['utm'], string> = {
  campaign: 'UTM Campaign',
  source: 'UTM Source',
  medium: 'UTM Medium',
  content: 'UTM Content',
  term: 'UTM Term'
}

export default function UtmListPage({ used_by }: IUtmListPageProps) {
  const [utms, setUtms] = useState<IUtm[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [filters, setFilters] = useState({ length: DEFAULT_PAGE_SIZE, utmType: '', search: '' })
  const isDirty = filters.utmType !== '' || filters.search !== ''

  // Trocar um filtro volta para a página 1: filtrar na página 5 pediria a página 5 do
  // resultado novo, quase sempre vazia.
  const updateFilters = (patch: Partial<typeof filters>) => {
    setFilters({ ...filters, ...patch })
    setPage(1)
  }
  const [showFormModal, setShowFormModal] = useState(false)
  const [editing, setEditing] = useState<IUtm | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: number | null }>({ open: false, id: null })
  const toast = useAppToast()
  const hasRole = useHasRole()

  const canManage = used_by === 'commercial' ? hasRole('manage_utms') : hasRole('manage_marketing_utms')

  const fetchUtms = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const data = await UrlShortenerService.listUtmsManagement(
        used_by,
        filters.utmType || undefined,
        filters.search || undefined,
        page,
        filters.length
      )
      setTotalPages(Math.ceil(data.recordsFiltered / filters.length) || 1)
      setUtms(data.data)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar as UTMs', error)
    } finally {
      setLoading(false)
    }
  }

  // Único efeito de busca: `updateFilters` volta para a página 1 no mesmo `setState`, então
  // isto roda uma vez. Antes, resetar a página e buscar na mesma passada disparava uma
  // requisição para a página antiga e outra para a página 1.
  useEffect(() => {
    fetchUtms()
  }, [page, used_by, filters])

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.id) return
    try {
      await UrlShortenerService.deleteUtmById(deleteDialog.id)
      toast.success('UTM removida com sucesso')
      fetchUtms()
    } catch {
      toast.error('Erro ao remover UTM')
    } finally {
      setDeleteDialog({ open: false, id: null })
    }
  }

  return (
    <>
      <FilterBar onClear={() => updateFilters({ utmType: '', search: '' })} isDirty={isDirty}>
        <FilterSearch
          value={filters.search}
          onChange={(search) => updateFilters({ search })}
          placeholder="Buscar por valor..."
        />
        <FilterSelect
          label="Tipo de UTM"
          maxW="200px"
          value={filters.utmType}
          onChange={(utmType) => updateFilters({ utmType })}
          options={(Object.entries(UTM_LABELS) as [IUtm['utm'], string][]).map(([value, label]) => ({ value, label }))}
        />
      </FilterBar>

      <TableContainer>
        <Table mt="2">
          <Thead>
            <Tr>
              <Th>UTM</Th>
              <Th>Valor</Th>
              <Th>Quantidade de utilizações</Th>
              <Th>Data de criação</Th>
              {canManage && <Th>Ações</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={5} />
            ) : loadError ? (
              <TableErrorRow colSpan={5} entity="UTMs" feminine onRetry={fetchUtms} />
            ) : utms.length === 0 ? (
              <TableEmptyRow colSpan={5} entity="UTM" feminine />
            ) : (
              utms.map((utm) => (
                <Tr key={utm.id}>
                  <Td>{UTM_LABELS[utm.utm]}</Td>
                  <Td>{utm.value}</Td>
                  <Td>{utm.usage_count}</Td>
                  <Td>{formatDateTime(utm.created_at)}</Td>
                  {canManage && (
                    <Td>
                      <ActionButton
                        action="edit"
                        label="Editar UTM"
                        onClick={() => {
                          setEditing(utm)
                          setShowFormModal(true)
                        }}
                      />
                      <ActionButton
                        action="delete"
                        label="Excluir UTM"
                        onClick={() => setDeleteDialog({ open: true, id: utm.id })}
                      />
                    </Td>
                  )}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(p: number) => setPage(p)}
        pageSize={filters.length}
        onPageSizeChange={(pageSize) => {
          setFilters({ ...filters, length: pageSize })
          setPage(1)
        }}
      />

      {canManage && (
        <ActionFab
          label="Adicionar UTM"
          onClick={() => {
            setEditing(null)
            setShowFormModal(true)
          }}
        />
      )}

      <UtmListFormModal
        isOpen={showFormModal}
        onClose={() => {
          setShowFormModal(false)
          setEditing(null)
        }}
        onSaved={fetchUtms}
        used_by={used_by}
        editing={editing}
      />

      <ConfirmDialog
        isOpen={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
        onConfirm={handleDeleteConfirm}
        action="delete"
        entity="UTM"
        feminine
        warning="As URLs curtas que já utilizam esta UTM não serão afetadas."
      />
    </>
  )
}
